import { useEffect, useRef } from "react";

/**
 * Runs `callback` every `intervalMs` while the tab is visible.
 * Also fires when the tab regains focus (nice-to-have freshness).
 * Callback receives no args; keep it side-effect only (data fetch).
 */
export default function useAutoRefresh(callback, intervalMs = 10 * 60 * 1000) {
  const cbRef = useRef(callback);
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    let id = null;
    const start = () => {
      stop();
      id = setInterval(() => {
        if (document.visibilityState === "visible") cbRef.current?.();
      }, intervalMs);
    };
    const stop = () => {
      if (id) clearInterval(id);
      id = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        cbRef.current?.();
        start();
      } else {
        stop();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
}
